import { useEffect, useState, createContext, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Heading, Text } from '../components/styledComponents';
import {
  getInboundBuilderDetails,
  getPrimaryInboundFromSubdomain,
  verifyInboundEnabled,
} from '../services/inbound.service';
import { getCreatorDetails } from '../services/dashboard.service';
import { isAuthenticated } from '../utils/auth-utils';
import ForbiddenVectorImage from '../assets/images/forbidden-vector-image.svg';
import LoaderSvg from '../assets/images/loader.svg';
import styles from '../styles/forbidden.module.css';
import { languageMap } from '../utils/translations';

// type SupportedLanguages = 'english' | 'french' | 'german' | 'spanish' | 'italian';
type SupportedLanguages = keyof typeof languageMap;

export type InboundDataType = {
  userId: string;
  selectedLanguage: SupportedLanguages;
  landingPage: {
    brandName: string;
    brandDesc: string;
    brandWebsite: string;
    ctaBtnTxt: string;
    headlineTxt: string;
    isActive: boolean;
    imageFill?: boolean;
    colors: {
      background: string;
      btnText: string;
      ctaBtn: string;
      headline: string;
      subText: string;
    };
    images: {
      logo: string;
      highlight: string;
    };
    fontStyle: string;
  };
  descriptionPage: {
    colors: {
      headline: string;
      subText: string;
    };
    isActive: boolean;
    info: {
      _id: string;
      headline: string;
      subText: string;
    }[];
  };
  formSetupPage: {
    firstName: boolean;
    lastName: boolean;
    email: boolean;
    isActive: boolean;
    socialLinks: {
      facebook: boolean;
      instagram: boolean;
      youtube: boolean;
      tiktok: boolean;
      twitter: boolean;
      blog: boolean;
    };
    giftOption: {
      isActive: boolean;
      productIds: [string];
    };
    address: boolean;
    colors: {
      btn: string;
      btnText: string;
    };
    payment: boolean;
    ambassadorProgramQuestions: [
      {
        _id: string;
        isActive: boolean;
        question: string;
        questionType: 'singleLineAnswer' | 'longAnswer' | 'yesNoAnswer' | 'radio' | 'checkbox';
        isRequired: boolean;
        options?: [{ name: string; _id: string }];
      }
    ];
    otherDetails: boolean;
    allowedPaymentMethods: string[];
    maxGiftSelection: number;
  };
  thanksPage: {
    headline: {
      emoji: string;
      txt: string;
      subTxt: string;
    };
    colors: {
      btn: string;
      btnText: string;
    };
    isActive: boolean;
  };
};

type InboundContextType = {
  creatorEmail: string;
  setCreatorEmail: React.Dispatch<React.SetStateAction<string>>;
  inboundData: InboundDataType | null;
  applicationId: string;
  setApplicationId: React.Dispatch<React.SetStateAction<string>>;
  defaultCurrency: string;
  t: (identifier: keyof (typeof languageMap)[SupportedLanguages]) => string;
  subDomainSuffix: string | undefined;
  setSubDomainSuffix: React.Dispatch<React.SetStateAction<string | undefined>>;
  formId: string | undefined;
  setFormId: React.Dispatch<React.SetStateAction<string | undefined>>;
  isNonAffiliate: boolean;
  setIsNonAffiliate: React.Dispatch<React.SetStateAction<boolean>>;
  homePageData: any;
  setHomePageData: React.Dispatch<React.SetStateAction<any>>;
  influencerAddress: any;
  setInfluencerAddress: React.Dispatch<React.SetStateAction<any>>;
};

export const InboundContext = createContext<InboundContextType>({
  creatorEmail: '',
  setCreatorEmail: () => {},
  inboundData: {} as InboundDataType,
  applicationId: '',
  setApplicationId: () => {},
  defaultCurrency: '',
  t: () => 'dummy',
  subDomainSuffix: undefined,
  setSubDomainSuffix: () => {},
  formId: undefined,
  setFormId: () => {},
  isNonAffiliate: false,
  setIsNonAffiliate: () => {},
  homePageData: null,
  setHomePageData: () => {},
  influencerAddress: {
    addressLineOne: '',
    addressLineTwo: '',
    pincode: '',
    city: '',
    state: '',
    country: '',
    phone: '',
  },
  setInfluencerAddress: () => {},
});

type ContextPropsType = {
  children: React.ReactNode | React.ReactNode[];
};

export function InboundProvider({ children }: ContextPropsType) {
  const [inboundData, setInboundData] = useState<InboundDataType | null>(null);
  const [creatorEmail, setCreatorEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [isDomainVerified, setIsDomainVerified] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [defaultCurrency, setDefaultCurrency] = useState('');
  const [fetchingData, setFetchingData] = useState(true);
  const [subDomainSuffix, setSubDomainSuffix] = useState<string | undefined>(undefined);
  const [formId, setFormId] = useState<string | undefined>(undefined);
  const [isNonAffiliate, setIsNonAffiliate] = useState<boolean>(false);
  const [homePageData, setHomePageData] = useState<any>(null);
  const [influencerAddress, setInfluencerAddress] = useState<any>({
    addressLineOne: '',
    addressLineTwo: '',
    pincode: '',
    city: '',
    state: '',
    country: '',
    phone: '',
  });

  const location = useLocation();
  const navigate = useNavigate();

  interface LandingDataInput {
    logo: string;
    highlight?: string;
    favicon?: string;
  }

  const getFaviconIcon = (data: LandingDataInput) => {
    if (data && data.favicon && data.favicon.length > 0) {
      return data.favicon;
    }

    return data.logo;
  };

  const fetchInboundData = async (suffix: string) => {
    const response = await getInboundBuilderDetails(suffix);
    if (response.success) {
      const data = response?.data;

      if (!data || !data?.thanksPage?.isActive) {
        setInboundData(null);
        setFetchingData(false);
        return;
      }

      setFormId(data?.formId);
      // change page title
      document.title = response.data.landingPage.headlineTxt;

      // change page favicon
      const faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      faviconLink.href = getFaviconIcon(response.data.landingPage.images);
      document.head.appendChild(faviconLink);

      // set Inbound data
      setInboundData({
        userId: data.userId,
        landingPage: data.landingPage,
        descriptionPage: data.descriptionPage,
        formSetupPage: data.formSetupPage,
        thanksPage: data.thanksPage,
        selectedLanguage: data.selectedLanguage,
      });
      setDefaultCurrency(data?.defaultCurrency);
      setFetchingData(false);
    } else {
      setInboundData(null);
      setFetchingData(false);
    }
  };
  const verifyDomain = async (suffix: string) => {
    const response = await verifyInboundEnabled(suffix);
    const getSuffix = location.pathname.split('/')[1];
    if (response.success || getSuffix !== '/dashboard') {
      setIsDomainVerified(true);
    } else {
      setIsDomainVerified(false);
    }
    setLoading(false);
  };

  const fetchPrimaryInboundFromSubdomain = async (subdomain: string) => {
    const response = await getPrimaryInboundFromSubdomain({ subdomain });
    if (response.status) {
      const data = response?.data?.subDomainSuffix;
      fetchInboundData(data);
      verifyDomain(data);
      navigate(`/${data}`);
    } else {
      setInboundData(null);
      setFetchingData(false);
    }
  };

  useEffect(() => {
    const getSuffix = location.pathname.split('/')[1];
    if (getSuffix) {
      setSubDomainSuffix(getSuffix);
    } else {
      const { hostname } = window.location;
      const subdomain = hostname.split('.')[0];
      fetchPrimaryInboundFromSubdomain(subdomain);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const fetchCreatorDetails = async () => {
    const response = await getCreatorDetails({});
    if (response.status) {
      setDefaultCurrency(response.data?.defaultCurrency);
      if (response?.data?.isNonAffiliate) {
        setIsNonAffiliate(true);
      }
    }
  };

  useEffect(() => {
    if (subDomainSuffix) {
      verifyDomain(subDomainSuffix);
      fetchInboundData(subDomainSuffix);
    }
    if (isAuthenticated()) {
      fetchCreatorDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subDomainSuffix]);

  const t = useCallback(
    (identifier: keyof (typeof languageMap)[SupportedLanguages]) => {
      let language: SupportedLanguages = 'english';
      if (inboundData?.selectedLanguage) {
        language = inboundData.selectedLanguage;
      }
      return languageMap[language][identifier];
    },
    [inboundData]
  );

  const inboundContextValues = useMemo(() => {
    return {
      inboundData,
      creatorEmail,
      setCreatorEmail,
      applicationId,
      setApplicationId,
      defaultCurrency,
      t,
      subDomainSuffix,
      setSubDomainSuffix,
      formId,
      setFormId,
      isNonAffiliate,
      setIsNonAffiliate,
      homePageData,
      setHomePageData,
      influencerAddress,
      setInfluencerAddress,
    };
  }, [
    inboundData,
    creatorEmail,
    setCreatorEmail,
    applicationId,
    setApplicationId,
    defaultCurrency,
    t,
    subDomainSuffix,
    setSubDomainSuffix,
    formId,
    setFormId,
    isNonAffiliate,
    setIsNonAffiliate,
    homePageData,
    setHomePageData,
    influencerAddress,
    setInfluencerAddress,
  ]);

  if (loading || (isAuthenticated() && !defaultCurrency)) {
    return (
      <div className="loader-div">
        <img src={LoaderSvg} alt="loader" width={100} />
      </div>
    );
  }

  if (fetchingData) {
    return null;
  }

  if (!isDomainVerified) {
    return (
      <div className={styles.container}>
        <div className={styles.leftContainer}>
          <img src={ForbiddenVectorImage} alt="" />
        </div>
        <div className={styles.rightContainer}>
          <Heading>Ouch!</Heading>
          <Text>You are not authorized to view this resource.</Text>
        </div>
      </div>
    );
  }

  return <InboundContext.Provider value={inboundContextValues}>{children}</InboundContext.Provider>;
}
